<template>
  <div>
    <!-- Group Header -->
    <div class="flex justify-between items-center gap-3 mb-3">
      <h3
        class="font-bold text-lg md:text-xl text-gray-900 break-words leading-tight flex items-center flex-wrap gap-2"
      >
        {{ reactiveGroup.name }}
        <span
          v-if="isRequired"
          class="text-xs normal-case bg-red-50 text-red-600 px-2 py-0.5 rounded-full font-semibold"
        >
          Required
        </span>
        <span
          v-else
          class="text-xs normal-case bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold"
        >
          Optional
        </span>
      </h3>
      <span class="text-sm text-gray-500 whitespace-nowrap">{{
        metaText
      }}</span>
    </div>

    <!-- Radio buttons for single selection -->
    <div
      v-if="reactiveGroup.max_selection === 1"
      class="grid grid-cols-1 gap-2"
    >
      <ModifierOption
        v-for="modifier in activeModifiers"
        :key="`${group.id}-${modifier.id}`"
        :modifier="modifier"
        :is-selected="isModifierSelected(modifier.id)"
        :is-disabled="
          isDisabled(modifier.id) || isModifierUnavailable(modifier)
        "
        :currency="currency"
        type="radio"
        @select="handleSelect(modifier)"
      />
    </div>

    <!-- Checkboxes for multiple selection -->
    <div v-else class="grid grid-cols-1 gap-2">
      <ModifierOption
        v-for="modifier in activeModifiers"
        :key="`${group.id}-${modifier.id}`"
        :modifier="modifier"
        :is-selected="isModifierSelected(modifier.id)"
        :is-disabled="
          isDisabled(modifier.id) || isModifierUnavailable(modifier)
        "
        :currency="currency"
        type="checkbox"
        @select="handleSelect(modifier)"
      />
    </div>
  </div>
</template>

<script>
import ModifierOption from "./ModifierOption.vue";

export default {
  name: "ModifierGroup",
  components: {
    ModifierOption
  },
  props: {
    group: {
      type: Object,
      required: true
    },
    groupIndex: {
      type: Number,
      required: true
    },
    modifierGroups: {
      type: Array,
      required: true
    },
    selectedModifiers: {
      type: Array,
      default: () => []
    },
    hasAttemptedSubmit: {
      type: Boolean,
      default: false
    },
    currency: {
      type: String,
      default: "$"
    }
  },
  computed: {
    // Get reactive group from modifierGroups array to ensure reactivity
    reactiveGroup() {
      return this.modifierGroups[this.groupIndex] || this.group;
    },
    activeModifiers() {
      const group = this.reactiveGroup;
      return (group.modifiers || []).filter((mod) => mod.active);
    },
    selectedCount() {
      const group = this.reactiveGroup;
      // For single selection, count is 0 or 1 based on selected_modifiers
      if (group.max_selection === 1) {
        return (group.selected_modifiers || []).length;
      }
      // For multiple selection, use selectedModifiers prop
      return this.selectedModifiers.filter((mod) => mod.group === group.id)
        .length;
    },
    // Computed property for selected modifiers in this group (for reactivity)
    // This computed ensures Vue tracks changes to group.selected_modifiers
    groupSelectedModifiers() {
      const group = this.reactiveGroup;
      if (group.max_selection === 1) {
        // Access selected_modifiers to ensure reactivity
        // Return a new array reference to force reactivity
        const selected = group.selected_modifiers || [];
        return [...selected]; // Create new array to ensure reactivity
      }
      return this.selectedModifiers.filter((mod) => mod.group === group.id);
    },
    // Computed map of selected modifier IDs for quick lookup
    selectedModifierIds() {
      return new Set(this.groupSelectedModifiers.map((m) => m.id));
    },
    isRequired() {
      return (this.reactiveGroup.min_selection || 0) > 0;
    },
    metaText() {
      const group = this.reactiveGroup;
      const min = group.min_selection || 0;
      const max = group.max_selection || 0;

      if (max === 1) {
        return "Pick 1";
      }
      if (min > 0) {
        if (max && max > min) {
          return `Pick ${min}–${max}`;
        }
        if (max === min) {
          return `Pick ${min}`;
        }
        return `Pick at least ${min}`;
      }
      if (max && max > 0) {
        return `Choose up to ${max}`;
      }
      return "Choose any";
    },
    showRequiredBadge() {
      return (
        this.hasAttemptedSubmit &&
        this.group.min_selection > 0 &&
        this.selectedCount < this.group.min_selection
      );
    },
    showDoneBadge() {
      return (
        this.group.min_selection > 0 &&
        this.selectedCount >= this.group.min_selection
      );
    }
  },
  methods: {
    isModifierSelected(modifierId) {
      // Use computed property for reactivity
      return this.selectedModifierIds.has(modifierId);
    },
    isSelected(modifierId) {
      // Alias for backward compatibility
      return this.isModifierSelected(modifierId);
    },
    isDisabled(modifierId) {
      if (this.isModifierSelected(modifierId)) return false;
      const group = this.reactiveGroup;
      // Single-select (radio) groups always allow switching to another option
      if (group.max_selection === 1) return false;
      // If max_selection is 0, null, or undefined, there's no limit
      if (!group.max_selection || group.max_selection === 0) return false;
      return this.selectedCount >= group.max_selection;
    },
    isModifierUnavailable(modifier) {
      return !!(modifier?.is_86d || modifier?.is86d);
    },
    handleSelect(modifier) {
      if (this.isModifierUnavailable(modifier)) return;
      const group = this.reactiveGroup;

      this.$emit("modifier-selected", {
        group: group,
        modifier
      });
    }
  }
};
</script>
